import { createSelector } from 'reselect';

import {
  FETCH_ERROR_MESSAGE,
  SECURE_MESSAGING_INBOX_BUTTON_LABEL,
  SECURE_MESSAGING_INBOX_HEADING_LABEL,
  SECURE_MESSAGING_NEW_MESSAGE_PATH_DICT,
} from './constants';
import {
  NEW_SECURE_MESSAGE_PROPS,
  RESOLVED_STATUS,
  SECURE_MESSAGING_PENDING_ISSUE,
} from '../Notifications/constants';
import { createNotification, createUnreadMessageNotifications } from '../Notifications/helpers';
import { initialState } from './reducer';
import { formatDate, formatErrorMessages } from '../../utils/globalHelpers';
import { SPLIT_JIRASECUREMESSAGING } from '../Main/constants';
import makeSelectMain, { getMainClientInfo } from '../Main/selectors';
import makeSelectMobile from '../Mobile/selectors';

/**
 * Direct selector to the SecureMessaging state domain
 */
const selectSecureMessagingDomain = (state) => state.secureMessaging || initialState;

/**
 * Default selector used by SecureMessaging
 */
const makeSelectSecureMessaging = (prop) => createSelector(
  selectSecureMessagingDomain,
  (messagesState) => messagesState[prop],
);

const selectConfirmationNumber = () => createSelector(
  selectSecureMessagingDomain,
  ({ confirmationNumber }) => confirmationNumber,
);

const selectErrors = (prop) => createSelector(
  selectSecureMessagingDomain,
  ({ errors }) => errors[prop],
);

const selectConfirmationData = (prop) => createSelector(
  selectSecureMessagingDomain,
  ({ confirmation }) => {
    const confirmationId = confirmation[prop];

    return ([{
      label: 'Confirmation Number',
      value: confirmationId,
    }]);
  },
);

const selectForm = (prop) => createSelector(
  selectSecureMessagingDomain,
  ({ forms }) => forms[prop],
);

const selectFormErrors = (prop) => createSelector(
  selectSecureMessagingDomain,
  ({ formErrors }) => formErrors[prop],
);

const selectHasUnreadSecureMessages = () => createSelector(
  makeSelectSecureMessaging('messages'),
  (messages) => messages.some(({ unreadReplyCount }) => unreadReplyCount > 0),
);

const selectIsMessageCached = () => createSelector(
  selectSecureMessagingDomain,
  ({ messageDetails, selectedMessageId }) => !!messageDetails[selectedMessageId],
);

const selectIsMessageStatusClosed = () => createSelector(
  selectSecureMessagingDomain,
  ({ messageDetails, selectedMessageId }) =>
    !!messageDetails[selectedMessageId] &&
    messageDetails[selectedMessageId].status === 'Resolved',
);

const selectIsSecureMessagingTreatmentOn = () => createSelector(
  makeSelectMain('treatments'),
  selectMessages(),
  makeSelectMain('isChangeSelectedLoanInProgress'),
  (treatments, messages, isChangeSelectedLoanInProgress) => {
    const { treatment: secureMessagingTreatment } =
      treatments[SPLIT_JIRASECUREMESSAGING] || {};
    return secureMessagingTreatment === 'on' || isChangeSelectedLoanInProgress || !!messages.length;
  },
);

const selectMessages = () => createSelector(
  selectSecureMessagingDomain,
  ({ messages }) => messages,
);

const selectRequestStatus = (prop) => createSelector(
  selectSecureMessagingDomain,
  ({ requestStatus }) => requestStatus[prop],
);

const selectSecureMessagingInquiryNotifications = () => createSelector(
  makeSelectSecureMessaging('messages'),
  (messages) => messages.filter(({ status }) => status !== RESOLVED_STATUS).map(
    ({ created }) => createNotification({
      date: formatDate(created, null, 'MMMM D, YYYY'),
      /**
       * Using 'created' timestamp for lastUpdated so notification remains in same
       * initial sorting position after message is updated, e.g. if reply is added.
       */
      lastUpdated: created,
      name: SECURE_MESSAGING_PENDING_ISSUE.name,
      text: SECURE_MESSAGING_PENDING_ISSUE.text,
    }),
  ),
);

/**
 * @description Selects errors and formatted form input errors for screen reader
 */
const selectSecureMessagingScreenReaderNotifications = () => createSelector(
  makeSelectSecureMessaging('errors'),
  makeSelectSecureMessaging('formErrors'),
  (
    {
      bannerError,
      serviceError: {
        fetchMessageDetails,
        fetchMessages,
        postMessage,
        postReply,
      },
    },
    {
      attachments,
      message,
      reply,
      uploadDocument,
    },
  ) => {
    const nonFormErrors = [
      bannerError,
      fetchMessageDetails,
      fetchMessages,
      postMessage,
      postReply,
    ];
    const notifications = nonFormErrors.filter((error) => !!error);
    notifications.concat(formatErrorMessages({
      attachments,
      ...message,
      ...reply,
      ...uploadDocument,
    }));

    return notifications;
  },
);

const selectSecureMessagingUnreadMessageNotifications = () => createSelector(
  makeSelectSecureMessaging('messages'),
  (messages) => createUnreadMessageNotifications({
    unreadMessages: messages.filter(({ unreadReplyCount }) => unreadReplyCount > 0)
      .map(({ unreadReplyDates }) => ({ unreadReplyDates })),
    ...NEW_SECURE_MESSAGE_PROPS,
  }),
);

const selectSelectedMessageDetails = () => createSelector(
  selectSecureMessagingDomain,
  ({ messageDetails, selectedMessageId }) => messageDetails[selectedMessageId] || {},
);

const selectStep = () => createSelector(
  selectSecureMessagingDomain,
  ({ step }) => step,
);

const selectAttachment = (prop) => createSelector(
  selectSecureMessagingDomain,
  ({ attachment }) => attachment[prop],
);

/**
 * @description selects error message with correct phone number
 */
const selectServiceErrorMessage = () => createSelector(
  getMainClientInfo('customerServicePhoneNumber'),
  (customerServicePhoneNumber) => `${FETCH_ERROR_MESSAGE} ${customerServicePhoneNumber}.`,
);

const selectSecureMessagingInboxProps = () => createSelector(
  selectRequestStatus('fetchMessages'),
  makeSelectMain('isChangeSelectedLoanInProgress'),
  makeSelectMobile('isMobile'),
  (requestStatus, isChangeSelectedLoanInProgress, isMobile) => {
    const { desktopPath, mobilePath } = SECURE_MESSAGING_NEW_MESSAGE_PATH_DICT;
    return {
      buttonLabel: SECURE_MESSAGING_INBOX_BUTTON_LABEL,
      buttonPath: isMobile ? mobilePath : desktopPath,
      error: requestStatus === 'failed',
      headingLabel: SECURE_MESSAGING_INBOX_HEADING_LABEL,
      loading: isChangeSelectedLoanInProgress || requestStatus === 'loading',
      shouldDisableNewMessageButton: isChangeSelectedLoanInProgress || requestStatus === 'failed',
    };
  },
);

export default makeSelectSecureMessaging;
export {
  selectAttachment,
  selectConfirmationData,
  selectConfirmationNumber,
  selectErrors,
  selectForm,
  selectFormErrors,
  selectHasUnreadSecureMessages,
  selectIsMessageCached,
  selectIsMessageStatusClosed,
  selectIsSecureMessagingTreatmentOn,
  selectMessages,
  selectRequestStatus,
  selectSecureMessagingDomain,
  selectSecureMessagingInboxProps,
  selectSecureMessagingInquiryNotifications,
  selectSecureMessagingScreenReaderNotifications,
  selectSecureMessagingUnreadMessageNotifications,
  selectSelectedMessageDetails,
  selectServiceErrorMessage,
  selectStep,
};
